import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';

function normalizeEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function isValidEmail(value) {
  const email = String(value || '').trim();
  return Boolean(email) && /\S+@\S+\.\S+/.test(email);
}

export function buildLessonInviteLinkPath(lessonId = '') {
  if (!lessonId) return '/lessons';
  return `/lessons/${lessonId}?invite=1`;
}

export function getInviteEligibility(lesson = {}) {
  const hasStudentId = Boolean(String(lesson?.studentId || '').trim());
  const hasEmail = isValidEmail(lesson?.studentEmail || lesson?.studentEmailNormalized || '');

  if (hasStudentId) {
    return {
      isEligible: false,
      inviteStatus: lesson?.inviteStatus === 'claimed' ? 'claimed' : 'not_needed',
    };
  }

  if (hasEmail) {
    return {
      isEligible: true,
      inviteStatus: lesson?.inviteStatus || 'pending',
    };
  }

  return {
    isEligible: false,
    inviteStatus: 'not_needed',
  };
}

export function deriveLessonInviteMetadata(lesson = {}, { lessonId = '' } = {}) {
  const normalizedStudentEmail = normalizeEmail(
    lesson?.studentEmailNormalized || lesson?.studentEmail || ''
  );
  const eligibility = getInviteEligibility(lesson);
  const effectiveLessonId = lesson?.lessonId || lessonId || '';
  const linkPath = effectiveLessonId ? buildLessonInviteLinkPath(effectiveLessonId) : '';

  return {
    inviteStatus: eligibility.inviteStatus,
    inviteSentAt: lesson?.inviteSentAt || null,
    inviteLinkPath: lesson?.inviteLinkPath || linkPath,
    inviteLastError: lesson?.inviteLastError || '',
    studentEmailNormalized: normalizedStudentEmail,
  };
}

export function buildLessonInviteMessage(lesson = {}, { appOrigin = '' } = {}) {
  const lessonId = lesson?.lessonId || '';
  const inviteLinkPath = lesson?.inviteLinkPath || buildLessonInviteLinkPath(lessonId);
  const inviteUrl = appOrigin ? `${String(appOrigin).replace(/\/$/, '')}${inviteLinkPath}` : inviteLinkPath;

  return {
    subject: 'A lesson has been created for you',
    body: `A lesson has been created for you in SAS. Open ${inviteUrl} to sign in or sign up and view it.`,
    inviteLinkPath,
    inviteUrl,
  };
}

export async function sendLessonInvite(db, lesson = {}, { appOrigin = '' } = {}) {
  const lessonId = lesson?.lessonId || '';
  const eligibility = getInviteEligibility(lesson);

  if (!lessonId || !eligibility.isEligible) {
    return {
      ok: false,
      status: eligibility.inviteStatus,
      reason: 'not_invite_eligible',
    };
  }

  if (lesson?.inviteStatus === 'sent') {
    return {
      ok: true,
      status: 'sent',
      duplicate: true,
      message: buildLessonInviteMessage(lesson, { appOrigin }),
    };
  }

  const message = buildLessonInviteMessage(lesson, { appOrigin });

  await updateDoc(doc(db, 'lessonInstances', lessonId), {
    inviteStatus: 'sent',
    inviteSentAt: serverTimestamp(),
    inviteLinkPath: message.inviteLinkPath,
    inviteLastError: '',
    studentEmailNormalized: normalizeEmail(lesson?.studentEmail || lesson?.studentEmailNormalized || ''),
  });

  return {
    ok: true,
    status: 'sent',
    duplicate: false,
    message,
  };
}
